import SimplePeer from 'simple-peer';
import { Socket } from 'socket.io-client';

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
export type EndReason = 'rejected' | 'busy' | 'unavailable' | 'hangup' | 'error' | null;

export interface CallerInfo {
  userId: string;
  name: string;
  avatar?: string;
}

export interface CallCallbacks {
  onStateChange: (state: CallState) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onIncomingCall: (callerInfo: CallerInfo) => void;
  onError: (err: Error) => void;
  onEndReason?: (reason: EndReason) => void;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'turn:openrelay.metered.ca:80',                username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443',               username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
  ...(process.env.REACT_APP_TURN_URL
    ? [{ urls: process.env.REACT_APP_TURN_URL, username: process.env.REACT_APP_TURN_USERNAME || '', credential: process.env.REACT_APP_TURN_CREDENTIAL || '' }]
    : [])
];

const VIDEO_SIGNAL_EVENT = 'video-signal';
const CONNECTION_TIMEOUT_MS = 15_000;

export class VideoCallService {
  private socket: Socket | null = null;
  private peer: SimplePeer.Instance | null = null;
  private localStream: MediaStream | null = null;
  private callbacks: CallCallbacks | null = null;
  private currentUserId: string = '';
  private activePeerId: string = '';
  private state: CallState = 'idle';
  private pendingSignals: any[] = [];
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private endReason: EndReason = null;

  private _onCallRequest = ({ callerId, callerInfo }: { callerId: string; callerInfo: CallerInfo }) => {
    if (this.state !== 'idle') {
      this.socket?.emit('call-busy', { callerId });
      return;
    }
    this.activePeerId = String(callerId);
    this._setState('ringing');
    this.callbacks?.onIncomingCall(callerInfo);
  };

  private _onCallAccept = async ({ accepterId }: { accepterId: string }) => {
    this.activePeerId = String(accepterId);
    await this._startPeer(true);
  };

  private _onCallReject     = () => { this._endCall('rejected'); };
  private _onCallEnd        = () => { this._endCall('hangup'); };
  private _onCallBusy       = () => { this._endCall('busy'); };
  private _onCallUnavailable = () => { this._endCall('unavailable'); };

  private _onVideoSignal = ({ fromUserId, signal }: { fromUserId: string; signal: any }) => {
    if (String(fromUserId) !== this.activePeerId) return;
    if (this.peer) {
      try { this.peer.signal(signal); } catch (_) {}
    } else {
      this.pendingSignals.push(signal);
    }
  };

  // ── Public API ────────────────────────────────────────────────────────────

  init(socket: Socket, userId: string, callbacks: CallCallbacks) {
    this._unbindSocketEvents();
    this.socket = socket;
    this.currentUserId = userId;
    this.callbacks = callbacks;
    this._bindSocketEvents();

    socket.on('reconnect', () => {
      this._unbindSocketEvents();
      this._bindSocketEvents();
      if (this.state !== 'idle' && this.state !== 'ended') this._endCall();
    });
  }

  async initiateCall(targetUserId: string, callerInfo: CallerInfo) {
    if (this.state !== 'idle') return;
    this.activePeerId = String(targetUserId);
    this._setState('calling');
    this.socket?.emit('call-request', { targetUserId, callerInfo });
  }

  // Callee: acquire media + create peer first, THEN emit call-accept.
  // This ensures the peer exists and can handle the initiator's first signals
  // without relying on the pendingSignals buffer.
  async acceptCall() {
    if (this.state !== 'ringing') return;
    await this._startPeer(false);
    this.socket?.emit('call-accept', { callerId: this.activePeerId });
  }

  rejectCall() {
    if (this.state !== 'ringing') return;
    this.socket?.emit('call-reject', { callerId: this.activePeerId });
    this._peerCleanup();
    this._setState('idle');
  }

  hangUp() {
    const peerId = this.activePeerId;
    this._peerCleanup();
    this.endReason = 'hangup';
    this.callbacks?.onEndReason?.('hangup');
    this._setState('ended');
    if (peerId) this.socket?.emit('call-end', { peerId });
    this._scheduleIdle();
  }

  getLocalStream(): MediaStream | null { return this.localStream; }
  getState(): CallState { return this.state; }
  getEndReason(): EndReason { return this.endReason; }

  // Track which camera is active so we can toggle without reading getSettings()
  private _facingMode: 'user' | 'environment' = 'user';

  /** Flip between front and rear camera during a connected call. */
  async switchCamera(): Promise<void> {
    if (!this.localStream || !this.peer) return;
    const currentTrack = this.localStream.getVideoTracks()[0];
    if (!currentTrack) return;

    const nextFacing = this._facingMode === 'user' ? 'environment' : 'user';

    // Use 'ideal' (not 'exact') so iOS/Android pick the primary front or rear camera
    // without cycling through virtual lenses (ultra-wide, telephoto, etc.)
    let newStream: MediaStream;
    try {
      newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: nextFacing } },
        audio: false,
      });
    } catch (e) {
      console.warn('[VideoCall] switchCamera failed:', e);
      return;
    }

    const newTrack = newStream.getVideoTracks()[0];
    if (!newTrack) return;

    // Replace track in the live peer connection (no renegotiation needed)
    const pc = (this.peer as any)._pc as RTCPeerConnection | undefined;
    if (pc) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
    }

    // Swap track in local stream so the self-preview updates
    currentTrack.stop();
    this.localStream.removeTrack(currentTrack);
    this.localStream.addTrack(newTrack);

    this._facingMode = nextFacing;
  }

  destroy() {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    if (this.connectionTimer) clearTimeout(this.connectionTimer);
    this._unbindSocketEvents();
    this._peerCleanup();
    this.callbacks = null;
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _setState(s: CallState) {
    this.state = s;
    this.callbacks?.onStateChange(s);
  }

  private _scheduleIdle(ms = 2000) {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.endReason = null;
      this.callbacks?.onEndReason?.(null);
      this._setState('idle');
    }, 2000);
  }

  private _endCall(reason: EndReason = 'hangup') {
    this._cancelConnectionTimeout();
    this._peerCleanup();
    this.endReason = reason;
    this.callbacks?.onEndReason?.(reason);
    this._setState('ended');
    // Show error messages longer so the user can read them
    this._scheduleIdle(reason === 'error' ? 8000 : 2000);
  }

  private _startConnectionTimeout() {
    this._cancelConnectionTimeout();
    this.connectionTimer = setTimeout(() => {
      if (this.state !== 'connected') {
        this.callbacks?.onError(new Error('Call timed out — could not establish connection'));
        this.hangUp();
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  private _cancelConnectionTimeout() {
    if (this.connectionTimer) { clearTimeout(this.connectionTimer); this.connectionTimer = null; }
  }

  private _bindSocketEvents() {
    if (!this.socket) return;
    this.socket.on('call-request',     this._onCallRequest);
    this.socket.on('call-accept',      this._onCallAccept);
    this.socket.on('call-reject',      this._onCallReject);
    this.socket.on('call-end',         this._onCallEnd);
    this.socket.on('call-busy',        this._onCallBusy);
    this.socket.on('call-unavailable', this._onCallUnavailable);
    this.socket.on(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private _unbindSocketEvents() {
    if (!this.socket) return;
    this.socket.off('call-request',     this._onCallRequest);
    this.socket.off('call-accept',      this._onCallAccept);
    this.socket.off('call-reject',      this._onCallReject);
    this.socket.off('call-end',         this._onCallEnd);
    this.socket.off('call-busy',        this._onCallBusy);
    this.socket.off('call-unavailable', this._onCallUnavailable);
    this.socket.off(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private async _startPeer(initiator: boolean) {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.callbacks?.onError(new Error('Media devices unavailable — site must be served over HTTPS'));
      this._endCall('error');
      return;
    }

    if (navigator.permissions?.query) {
      try {
        const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (mic.state === 'denied') {
          this.callbacks?.onError(new Error('Microphone permission denied — enable it in browser/app settings'));
          this._endCall('error');
          return;
        }
      } catch (_) {}
    }

    // Acquire media, then check if the call is still live before proceeding.
    // Without this guard, a stream acquired during a cancelled call leaks
    // and keeps the browser mic/cam indicator on.
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      } catch (err: any) {
        const n = err?.name || '';
        this.callbacks?.onError(new Error(
          n === 'NotAllowedError' ? 'Camera/microphone access denied — click the camera icon in your browser address bar to allow, then call again' :
          n === 'NotFoundError'  ? 'No microphone found on this device' :
          `Could not access camera/microphone (${n || 'unknown error'})`
        ));
        this._endCall('error');
        return;
      }
    }

    if (this.state === 'idle' || this.state === 'ended') {
      stream?.getTracks().forEach(t => t.stop());
      return;
    }
    this.localStream = stream;

    this.peer = new SimplePeer({
      initiator,
      stream: this.localStream,
      trickle: true,
      config: { iceServers: ICE_SERVERS },
    });

    // Start ICE timeout now — media is ready, peer exists, negotiation begins here.
    // Starting it earlier (before getUserMedia) penalises PC/iPad users who see a
    // permission dialog before the stream is granted.
    this._startConnectionTimeout();

    while (this.pendingSignals.length) {
      try { this.peer.signal(this.pendingSignals.shift()); } catch (_) {}
    }

    this.peer.on('signal', (data) => {
      this.socket?.emit(VIDEO_SIGNAL_EVENT, { targetUserId: this.activePeerId, signal: data });
    });

    this.peer.on('stream', (stream) => {
      this._cancelConnectionTimeout();
      this._setState('connected');
      this.callbacks?.onRemoteStream(stream);
    });

    this.peer.on('error', (err) => {
      this.callbacks?.onError(err);
      this._endCall('error');
    });

    this.peer.on('close', () => {
      if (this.state === 'connected' || this.state === 'calling' || this.state === 'ringing') {
        this._endCall();
      }
    });
  }

  private _peerCleanup() {
    this._cancelConnectionTimeout();
    this.peer?.destroy();
    this.peer = null;
    this.localStream?.getTracks().forEach(t => t.stop());
    this.localStream = null;
    this.activePeerId = '';
    this.pendingSignals = [];
    this._facingMode = 'user'; // reset to front camera for next call
  }
}

export const videoCallService = new VideoCallService();
